// Retention policy — the client half of the rules the database already holds.
//
// Postgres enforces all of this in firms_retention_window_check
// (migration 20260908152002): each window is either NULL or 7–3650 days, and
// the identity window is never shorter than the interval window. A CHECK
// violation reaches the browser as an unreadable constraint name, so the same
// three rules are stated here in words a firm admin can act on. This file is
// pure so it can be tested without a database or a browser.

export const RETENTION_MIN_DAYS = 7;
export const RETENTION_MAX_DAYS = 3650;

/** Null means the timer is off: nothing is ever purged automatically. */
export type RetentionDays = number | null;

/**
 * Why the pair is invalid, in one sentence, or null when it is fine.
 *
 * Off (null) on either timer is always valid — off is the default and stays
 * the default. The ordering rule only binds when both are on: identity must
 * not outlive the interval data it identifies.
 */
export function validateRetentionPolicy(
  intervalsDays: RetentionDays,
  identityDays: RetentionDays
): string | null {
  for (const [label, value] of [
    ['Delete raw interval data after', intervalsDays],
    ['Remove customer identity after', identityDays],
  ] as const) {
    if (value === null) continue;
    if (!Number.isInteger(value)) {
      return `${label}: enter a whole number of days, or switch it Off.`;
    }
    if (value < RETENTION_MIN_DAYS || value > RETENTION_MAX_DAYS) {
      return `${label}: must be between ${RETENTION_MIN_DAYS} and ${RETENTION_MAX_DAYS} days, or Off.`;
    }
  }

  if (
    intervalsDays !== null &&
    identityDays !== null &&
    identityDays < intervalsDays
  ) {
    return 'Customer identity cannot be removed before the interval data it identifies. Set the identity window to at least the interval window, or switch one of them Off.';
  }

  return null;
}

/** "Off", "7 days", "365 days" — for read-only display of a stored window. */
export function formatRetentionWindow(days: RetentionDays): string {
  if (days === null || days === undefined) return 'Off';
  return `${days} day${days === 1 ? '' : 's'}`;
}

// ── The audit report ────────────────────────────────────────────────────────
// Shape of get_firm_retention_report(). Every field is optional on the way in
// because the RPC's jsonb is not type-checked by PostgREST, and a panel that
// throws on a missing key is a panel that cannot be screenshotted during an
// audit.

export interface RetentionReport {
  generatedAt: string | null;
  policyIntervalsDays: RetentionDays;
  policyIdentityDays: RetentionDays;
  policyEnabled: boolean;
  projectsCompleted: number;
  projectsIntervalsPurged: number;
  projectsIdentityRedacted: number;
  oldestCompletedStillIdentified: string | null;
  actionsLast90Days: { action: string; count: number }[];
  orphanedIdentityRecords: number;
}

function asDays(value: unknown): RetentionDays {
  const n = Number(value);
  return value === null || value === undefined || !Number.isFinite(n) ? null : n;
}

function asCount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function asText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** Turn the RPC's jsonb into figures the panel can print without guarding. */
export function parseRetentionReport(raw: unknown): RetentionReport {
  const root = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const policy = (root.policy && typeof root.policy === 'object' ? root.policy : {}) as Record<string, unknown>;
  const projects = (root.projects && typeof root.projects === 'object' ? root.projects : {}) as Record<string, unknown>;
  const actions = (root.actions_last_90_days && typeof root.actions_last_90_days === 'object'
    ? root.actions_last_90_days
    : {}) as Record<string, unknown>;

  return {
    generatedAt: asText(root.generated_at),
    policyIntervalsDays: asDays(policy.intervals_days),
    policyIdentityDays: asDays(policy.identity_days),
    policyEnabled: policy.enabled === true,
    projectsCompleted: asCount(projects.completed),
    projectsIntervalsPurged: asCount(projects.intervals_purged),
    projectsIdentityRedacted: asCount(projects.identity_redacted),
    oldestCompletedStillIdentified: asText(projects.oldest_completed_still_identified),
    actionsLast90Days: Object.entries(actions)
      .map(([action, count]) => ({ action, count: asCount(count) }))
      .sort((a, b) => b.count - a.count || a.action.localeCompare(b.action)),
    orphanedIdentityRecords: asCount(root.orphaned_identity_records),
  };
}

/** Human label for a retention_events.action value. Unknown actions fall back. */
export function formatRetentionAction(action: string): string {
  const labels: Record<string, string> = {
    intervals_purged: 'Raw interval data purged',
    identity_redacted: 'Customer identity removed',
    orphans_swept: 'Orphaned identity records redacted',
  };
  if (labels[action]) return labels[action];
  const spaced = action.replace(/_/g, ' ').trim();
  if (!spaced) return 'Unknown action';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
